/**
 * Public header - Sleek green/black theme.
 * Glassmorphism, scroll-reactive, animated nav.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext, useI18n } from '../../providers';
import Logo from '../common/Logo';
import Dropdown from '../common/Dropdown';
import { ROUTES } from '../../utils/constants';

const PublicHeader = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuthContext();
  const { language, setLanguage } = useI18n();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const navLinks = [
    { label: language === 'fr' ? 'Comment ça marche' : 'How It Works', path: ROUTES.HOW_IT_WORKS },
    { label: language === 'fr' ? 'Tarifs' : 'Pricing', path: ROUTES.PRICING },
    { label: language === 'fr' ? 'À propos' : 'About', path: ROUTES.ABOUT },
    { label: language === 'fr' ? 'Contact' : 'Contact', path: ROUTES.CONTACT },
  ];

  const userMenuItems = isAuthenticated
    ? [
        { label: language === 'fr' ? 'Tableau de bord' : 'Dashboard', onClick: () => navigate(ROUTES.DASHBOARD) },
        { label: language === 'fr' ? 'Mon profil' : 'My Profile', onClick: () => navigate(ROUTES.PROFILE) },
        { label: language === 'fr' ? 'Paramètres' : 'Settings', onClick: () => navigate(ROUTES.SETTINGS) },
      ]
    : [];

  const go = (path, closeMobile = false) => {
    navigate(path);
    if (closeMobile) setMobileOpen(false);
  };

  return (
    <header className={`header-sleek ${scrolled ? 'is-scrolled' : ''}`}>
      <div className="header-sleek-inner">
        <button type="button" onClick={() => navigate(ROUTES.HOME)} className="header-sleek-logo" aria-label="WekaTrust Home">
          <Logo size="header" />
        </button>

        <nav className="header-sleek-nav" aria-label="Main">
          {navLinks.map((link) => (
            <button key={link.path} type="button" onClick={() => go(link.path)} className="header-sleek-link">
              {link.label}
            </button>
          ))}
        </nav>

        <div className="header-sleek-actions">
          <button type="button" onClick={() => setLanguage(language === 'fr' ? 'en' : 'fr')} className="header-sleek-lang" aria-label="Toggle language">
            {language === 'fr' ? 'EN' : 'FR'}
          </button>

          {isAuthenticated ? (
            <Dropdown
              trigger={
                <button type="button" className="header-sleek-user" aria-expanded="false" aria-haspopup="true">
                  <span className="header-sleek-user-avatar">
                    {user?.name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'U'}
                  </span>
                  <span className="header-sleek-user-name">
                    {user?.name || user?.fullName || (language === 'fr' ? 'Utilisateur' : 'User')}
                  </span>
                  <svg className="header-sleek-user-chevron" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              }
              items={userMenuItems.map((item) => ({ label: item.label, onClick: item.onClick }))}
              position="bottom-right"
            />
          ) : (
            <>
              <button type="button" onClick={() => navigate(ROUTES.LOGIN)} className="header-sleek-login">
                {language === 'fr' ? 'Connexion' : 'Login'}
              </button>
              <button type="button" onClick={() => (isAuthenticated ? navigate(ROUTES.DASHBOARD) : navigate(ROUTES.SIGNUP))} className="header-sleek-cta">
                {language === 'fr' ? 'Commencer' : 'Get Started'}
              </button>
            </>
          )}

          <button
            type="button"
            onClick={() => setMobileOpen((o) => !o)}
            className="header-sleek-burger"
            aria-label={language === 'fr' ? 'Menu' : 'Menu'}
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? (
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>
      </div>

      <div className={`header-sleek-mobile ${mobileOpen ? 'is-open' : ''}`} aria-hidden={!mobileOpen}>
        <div className="header-sleek-mobile-inner">
          {navLinks.map((link) => (
            <button key={link.path} type="button" onClick={() => go(link.path, true)} className="header-sleek-mobile-link">
              {link.label}
            </button>
          ))}
          {!isAuthenticated && (
            <>
              <div className="header-sleek-mobile-divider" />
              <button type="button" onClick={() => go(ROUTES.LOGIN, true)} className="header-sleek-mobile-link">
                {language === 'fr' ? 'Connexion' : 'Login'}
              </button>
              <button type="button" onClick={() => go(ROUTES.SIGNUP, true)} className="header-sleek-mobile-cta">
                {language === 'fr' ? 'Commencer' : 'Get Started'}
              </button>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export default PublicHeader;
